import React, { useRef, useLayoutEffect, useMemo } from 'react';
import { formatInlineMarkdown } from './formatInlineMarkdown';

/**
 * LineReveal — Harvey-AI-style line-by-line animated reveal.
 *
 * Each time a new \n-separated line appears in `text`, it slides in from
 * slightly below with a spring fade. Already-displayed lines stay static.
 * Inline markdown (**bold**, *italic*, `code`) is preserved.
 *
 * @param {string}  text        Full text (sentences separated by \n).
 * @param {boolean} isStreaming True while the backend is still sending.
 */
const LineReveal = React.memo(function LineReveal({ text, isStreaming }) {
    // Track how many visible lines existed in the previous render.
    // useLayoutEffect fires synchronously after DOM paint — safe to update here.
    const prevCountRef = useRef(0);

    const lines = useMemo(() => {
        if (!text) return [];
        return text
            .split('\n')
            .map((raw, originalIdx) => ({ text: raw.trim(), key: originalIdx }))
            .filter((l) => l.text.length > 0);
    }, [text]);

    // Snapshot BEFORE updating so this render can compare against prev
    const newLineStart = prevCountRef.current;

    useLayoutEffect(() => {
        prevCountRef.current = lines.length;
    });

    if (!lines.length) return null;

    return (
        <div className="line-reveal">
            {lines.map((line, i) => {
                const isNew = i >= newLineStart;
                // Stagger: each newly appeared line delays 45ms more than the previous
                const delay = isNew ? (i - newLineStart) * 45 : 0;

                // Last line while streaming → show a blinking cursor at the end
                const showCursor = isStreaming && i === lines.length - 1;

                return (
                    <div
                        key={line.key}
                        className={`line-reveal__line${isNew ? ' line-reveal__line--enter' : ''}${showCursor ? ' line-reveal__line--sheen' : ''}`}
                        style={isNew ? { animationDelay: `${delay}ms` } : undefined}
                    >
                        <span className="line-reveal__bar" aria-hidden="true" />
                        <span className="line-reveal__text">
                            {formatInlineMarkdown(line.text)}
                            {showCursor && (
                                <span className="line-reveal__cursor" aria-hidden="true" />
                            )}
                        </span>
                    </div>
                );
            })}
        </div>
    );
});

export default LineReveal;

